// Reading dose patterns for the printed prescription and discharge summary:
// "1-0-1" → which times of day, "Q8H" → every 8 hours, "BD" → twice a day.

type Labels = Record<string, string | undefined>;

const SLOTS_3 = ['morning', 'afternoon', 'night'];
const SLOTS_4 = ['morning', 'afternoon', 'evening', 'night'];
const NAMED: Record<string, string[]> = {
  od: ['morning'], bd: ['morning', 'night'], bid: ['morning', 'night'],
  tds: SLOTS_3, tid: SLOTS_3, qid: SLOTS_4, qds: SLOTS_4,
};
const PER_DAY: Record<string, number> = { od: 1, bd: 2, bid: 2, tds: 3, tid: 3, qid: 4, qds: 4 };

const capitalise = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

/** Nothing to take at this time: "", "0", "0 drops", "0ml", "0.0" — whatever the unit */
export function isZeroDose(part: string): boolean {
  const v = part.trim().replace('½', '0.5').replace('¼', '0.25').replace('¾', '0.75');
  if (!v) return true;
  const m = v.match(/^(\d+(?:\.\d+)?)/);
  return m ? Number(m[1]) === 0 : false;   // no leading number ("SOS") — something to take
}

/** The parts of a pattern: "1-0-1", "1–0–1", "1 + 0 + 1", "1 0 1", "1-0-1-" → ["1","0","1"]; null when it isn't one */
export function doseParts(dosage: string): string[] | null {
  let d = dosage.trim().replace(/[–—+]/g, '-').replace(/^[-\s]+|[-\s]+$/g, '');
  if (!d.includes('-') && /^\d+(\.\d+)?(\s+\d+(\.\d+)?){2,5}$/.test(d)) d = d.split(/\s+/).join('-');
  const parts = d.split('-').map(p => p.trim());
  return parts.length >= 3 && parts.length <= 6 ? parts : null;
}

const timesADay = (n: number, t: Labels) => (t.timesADay ?? '{n} times a day').replace('{n}', String(n));
const everyHours = (h: string, t: Labels) => (t.everyNHours ?? 'Every {n} hours').replace('{n}', h);

/**
 * What a dose means, in the print language: "Morning · Night", "4 times a day", "Every 8 hours".
 * Empty for doses without a pattern ("Apply twice daily", "1 amp") — they print as written.
 */
export function doseMeaning(dosage: string, t: Labels): string {
  const d = dosage.trim().toLowerCase().replace(/\s+/g, ' ');
  const label = (slot: string) => t[slot] ?? capitalise(slot);
  if (d === 'sos' || d === 'prn') return t.sos ?? 'SOS';
  if (d === 'as directed') return t.asDirected ?? 'As Directed';
  if (d === 'hs' || d === 'bedtime' || d === 'od hs') return t.bedtime ?? 'At Bedtime';
  if (NAMED[d]) return NAMED[d].map(label).join(' · ');
  const q = d.match(/^q ?(\d+) ?h(rs?|ourly)?$/);
  if (q) return everyHours(q[1], t);

  const parts = doseParts(dosage);
  if (!parts) return '';
  const slots = parts.length === 3 ? SLOTS_3 : parts.length === 4 ? SLOTS_4 : null;
  if (slots) return parts.map((p, i) => (isZeroDose(p) ? '' : label(slots[i]))).filter(Boolean).join(' · ');
  // 5 or 6 times a day: no names for the times, so the count
  const n = parts.filter(p => !isZeroDose(p)).length;
  return n ? timesADay(n, t) : '';
}

/** A frequency code in words, for the discharge summary: BD → "Twice a day", Q6H → "Every 6 hours" */
export function frequencyMeaning(frequency: string, t: Labels): string {
  const f = frequency.trim().toLowerCase().replace(/\s+/g, '');
  const n = PER_DAY[f];
  if (n === 1) return t.onceADay ?? 'Once a day';
  if (n === 2) return t.twiceADay ?? 'Twice a day';
  if (n) return timesADay(n, t);
  const q = f.match(/^q(\d+)h(rs?|ourly)?$/);
  if (q) return everyHours(q[1], t);
  if (f === 'sos' || f === 'prn') return t.whenNeeded ?? 'When needed';
  if (f === 'hs') return t.bedtime ?? 'At Bedtime';
  if (f === 'weekly') return t.onceAWeek ?? 'Once a week';
  return '';
}
